import { useQuery } from "@tanstack/react-query";
import { STORAGEKEY } from "@/lib/curio";
import { connect as connectRequest } from "@/api/session";
import { ConnectionError, DestinataryNotFoundError } from "@curio/client/errors";

export const useConnectQuery = () => {
  const hasToken = !!sessionStorage.getItem(STORAGEKEY);

  return useQuery({
    queryKey: ["auth", "connect"],
    queryFn: async () => {
      const token = sessionStorage.getItem(STORAGEKEY);

      if (!token) {
        throw new Error("No token found");
      }

      // 8000ms original era mais curto que a lentidão REAL e já documentada do backend neste
      // ambiente (harness, cli/web/harness/29-.../specs/2026-09-21-backend-timeout-504-relatorio.md):
      // a checagem de sessão (_SignalName "16") já foi observada levando 13-25s, com sucesso (HTTP
      // 200), mais de uma vez. Com o timeout curto + `retry: 1` abaixo, uma sessão VÁLIDA era tratada
      // como inválida (usuário jogado pro login) sempre que o backend só estava lento, não
      // realmente fora do ar -- confirmado ao vivo (2026-09-22, suíte `menu-permissoes`): as 2
      // tentativas (a original + o retry) voltaram 200 alguns segundos DEPOIS do cliente já ter
      // desistido de cada uma. 30000ms cobre com folga os 2 casos reais já vistos e ainda fica bem
      // abaixo do teto real de ~60s do proxy (nginx) documentado no relatório -- uma conexão
      // genuinamente morta continua limitada a um tempo razoável, só não mais curta que a lentidão
      // normal deste ambiente.
      const CONNECT_TIMEOUT_MS = 30_000;
      const result = await Promise.race([
        connectRequest(token),
        new Promise((_, reject) => setTimeout(() => reject(new Error("Connection timeout")), CONNECT_TIMEOUT_MS))
      ]);

      if (result instanceof DestinataryNotFoundError || result instanceof ConnectionError) {
        throw result;
      }

      if (result instanceof Error || !result) {
        throw result ?? new Error("Connection failed");
      }

      return result;
    },
    retry: 1,
    refetchOnWindowFocus: false,
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000
  });
};
